import { useEffect, useState } from 'react';
import { LuMessageSquare } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { ChatSession, getProjectSessions } from '../../shared/api/chat-sessions';
import { useProjects, useProjectsMeta } from '../../shared/contexts/Projects.provider';
import { auth } from '../../shared/firebase/firebase';
import { useStandaloneProjects } from '../../shared/hooks/use-standalone-projects';
import { useTrelloBoards } from '../../shared/hooks/use-trello-boards';
import { resolveApiProjectId, VIRTUAL_PROJECTS } from '../../shared/utils/virtual-projects';

interface SessionWithProject extends ChatSession {
  projectName: string;
}

const MAX_PROJECTS = 5;
const MAX_SHOWN = 6;

const SessionListSmall = () => {
  const navigate = useNavigate();
  const projects = useProjects();
  const { isReady } = useProjectsMeta();
  const { boards: trelloBoards } = useTrelloBoards();
  const { projects: standaloneProjects } = useStandaloneProjects();
  const userId = auth.currentUser?.uid ?? '';

  const [sessions, setSessions] = useState<SessionWithProject[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isReady) return;

    setIsLoading(true);
    const projectSlice = projects.slice(0, MAX_PROJECTS);
    const boardSlice = trelloBoards.slice(0, MAX_PROJECTS);
    const standaloneSlice = standaloneProjects.slice(0, MAX_PROJECTS);

    Promise.allSettled([
      ...projectSlice.map((p) => {
        const id = p._id ?? p.id ?? '';
        return getProjectSessions(id).then((list) => list.map((s) => ({ ...s, projectName: p.name ?? id })));
      }),
      ...boardSlice.map((b) =>
        getProjectSessions(b.id).then((list) => list.map((s) => ({ ...s, projectName: `${b.name} (Trello)` }))),
      ),
      ...standaloneSlice.map((p) =>
        getProjectSessions(p._id).then((list) => list.map((s) => ({ ...s, projectName: `${p.name} (Standalone)` }))),
      ),
      // Virtual buckets: override `projectId` back to the routing key ('standalone'/'wizard') —
      // the actual stored session.projectId is the resolved `${type}-${userId}` sentinel, which
      // SessionChatPage doesn't recognize as virtual.
      ...VIRTUAL_PROJECTS.map((v) =>
        getProjectSessions(resolveApiProjectId(v.id, userId)).then((list) =>
          list.map((s) => ({ ...s, projectId: v.id, projectName: v.label })),
        ),
      ),
    ])
      .then((results) => {
        const all: SessionWithProject[] = [];
        for (const r of results) {
          if (r.status === 'fulfilled') all.push(...r.value);
        }
        all.sort((a, b) => b.updatedAt - a.updatedAt);
        setSessions(all.slice(0, MAX_SHOWN));
      })
      .finally(() => setIsLoading(false));
  }, [isReady, projects, trelloBoards, standaloneProjects, userId]);

  if (!isReady || isLoading) {
    return (
      <div className="space-y-2">
        <div className="skeleton h-7 w-full" />
        <div className="skeleton h-7 w-full" />
        <div className="skeleton h-7 w-3/4" />
      </div>
    );
  }

  if (sessions.length === 0) {
    return <div className="text-xs opacity-50 px-1">No sessions yet</div>;
  }

  return (
    <div className="flex flex-col gap-1">
      {sessions.map((s) => {
        const title = s.title ?? new Date(s.createdAt).toLocaleDateString();
        return (
          <button
            key={s._id}
            className="btn btn-ghost btn-sm w-full justify-start px-2 rounded-xl gap-2"
            title={`${s.projectName} — ${title}`}
            onClick={() => navigate(`/chat/sessions/${s._id}?projectId=${s.projectId}`)}
          >
            <LuMessageSquare size={13} className="opacity-50 shrink-0" />
            <div className="flex flex-col items-start min-w-0">
              <span className="truncate text-xs w-full">{title}</span>
              <span className="truncate text-xs opacity-40 w-full">{s.projectName}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default SessionListSmall;
